import { call, put, select, take, fork, cancel, race } from 'redux-saga/effects';
import {
  changeStatusAfterUpdate,
  generateReportFailure,
  generateReportSuccess,
  getDataPageRequest,
  getTransactionsFilters,
} from '../actions';
import { ApiError } from 'store/api/types';
import { GenerateReportRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import {
  CheckEntityStatusResponse,
  GenerateReportResponse,
  checkEntityStatus,
  generateReport,
  refreshReport,
} from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { DataStatus, VatReportData } from '../models';
import { DataLogStatus } from 'store/app/enums';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { SET_CURRENT_JOURNALS_PAGE } from '../constants';
import { JournalType } from '../models/vatReportData';
import { CANCEL_VAT_SAGA, GENERATE_REPORT_REQUEST } from '../constants';
import { getVatRunData, getVatReportData } from '../selectors';
import { StoreState } from 'store/types';
import { DataContact } from '../models/vatReportData';
import i18n from 'i18next';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* generateReportSaga(action: GenerateReportRequestAction) {
  try {
    let err: ApiError | null;
    const {
      vatGenerateRequestData,
      totalTransactions,
      reportId,
      prefetchTransactionsPageSize,
      prefetchExceptionsPageSize,
      skipJournalPages,
    } = action.payload;
    const statusCheckResponse: CheckEntityStatusResponse = yield call(
      checkEntityStatus,
      vatGenerateRequestData.connectionId
    );
    if (
      statusCheckResponse.data.status !== DataStatus.IDLE &&
      statusCheckResponse.data.status !== null // TODO: remove this once we have a proper way to check the status
    ) {
      const message: MessageData = {
        title: i18n.t('notifications.server-busy'),
        description: i18n.t('notifications.we-are-currently-updating-the-data-from-the-provider-please-try-'),
        type: MessageStates.WARNING,
      };
      yield put(turnMessageOn(message));
      yield put(generateReportFailure());
      //} else if (statusCheckResponse.data.status === DataStatus.IDLE) {
    } else {
      let newTotals = 0;
      let noDataMessage = '';
      let isEmpty = false;
      let lastCashAccountingId = 1;
      let lastAccrualsId = 1;
      let useSummarized = false;

      do {
        const isFirstPage = lastCashAccountingId === 1 && lastAccrualsId === 1;
        const response: GenerateReportResponse = yield call(
          generateReport,
          vatGenerateRequestData,
          lastAccrualsId,
          lastCashAccountingId,
          reportId
        );

        err = checkResponse(response?.responseMessage);
        if (err) throw err;

        if (response?.data?.status == 'RETRY_AFTER') {
          noDataMessage = response?.responseMessage?.message;
          isEmpty = true;
        } else {
          const vatReportData: VatReportData =
            Object.keys(response.data).length > 0
              ? response.data
              : ({
                  empty: true,
                } as VatReportData);

          if (vatReportData.empty || vatReportData.recap) {
            isEmpty = true;
          } else {
            lastAccrualsId = vatReportData.accrualsLastId ?? 1;
            lastCashAccountingId = vatReportData.cashAccountingLastId ?? 1;
          }

          yield put(generateReportSuccess(vatReportData, isEmpty, isFirstPage));
          newTotals =
            newTotals +
            vatReportData.transactionsSize +
            vatReportData.exceptionsSize;

          useSummarized = vatReportData?.summarizedSize > 0;
        }
      } while (!isEmpty);

      if (noDataMessage) {
        const message: MessageData = {
          title: i18n.t('notifications.server-message-2'),
          description: noDataMessage,
          type: MessageStates.WARNING,
        };
        yield put(turnMessageOn(message));
        const noDataFound: boolean = true;
        yield put(generateReportFailure(noDataFound));
        //yield put(push(routes.main));
      } else {
        if (!skipJournalPages) {
          //fetch filters
          yield put(
            getTransactionsFilters(
              vatGenerateRequestData?.provider,
              vatGenerateRequestData?.connectionId,
              vatGenerateRequestData?.mappingId
            )
          );

          yield call(
            callInitialPages,
            reportId,
            prefetchTransactionsPageSize,
            prefetchExceptionsPageSize,
            useSummarized
          );

          const difference = newTotals - totalTransactions;
          if (difference > 0 && totalTransactions !== 0) {
            yield call(showNewTransactionsMessage, difference, false);
          }
        }

        const vatRunData: ReturnType<typeof getVatRunData> = yield select(
          (state: StoreState) => getVatRunData(state)
        );
        const currentStatus = vatRunData?.status;
        const statusValue = currentStatus
          ? DataLogStatus[currentStatus as keyof typeof DataLogStatus] ??
            currentStatus
          : undefined;
        const mayMarkDraft =
          !currentStatus ||
          statusValue === DataLogStatus.NOT_STARTED ||
          statusValue === DataLogStatus.NOT_SAVED;

        if (mayMarkDraft) {
          yield put(changeStatusAfterUpdate(DataLogStatus.NOT_SAVED));
        }
      }
    }
  } catch (error: any) {
    // Attach connectionId/mappingId so "New tax rates detected" message can link to mapping page
    const { vatGenerateRequestData } = action.payload;
    const errorWithContext = {
      ...error,
      connectionId: vatGenerateRequestData?.connectionId,
      mappingId: vatGenerateRequestData?.mappingId,
    };
    const message: MessageData | null = renderErrorMessage(errorWithContext);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(generateReportFailure());
  }
}

export function* watchGenerateReportSaga() {
  let task;
  while (true) {
    const action = yield take(GENERATE_REPORT_REQUEST);
    if (task) {
      yield cancel(task);
    }
    task = yield fork(generateReportSaga, action);

    // Race between saga completion and cancellation
    const result = yield race({
      completed: task,
      cancelled: take(CANCEL_VAT_SAGA),
    });

    if (result.cancelled) {
      yield cancel(task);
    }
  }
}

export function* refreshReportSaga(action: any) {
  try {
    const {
      vatGenerateRequestData,
      reportId,
      totalTransactions,
      prefetchTransactionsPageSize,
      prefetchExceptionsPageSize,
      transactionsType,
      skipJournalPages,
    } = action.payload;

    const previousReportData: ReturnType<typeof getVatReportData> = yield select(
      (state: StoreState) => getVatReportData(state)
    );

    const response: GenerateReportResponse = yield call(
      refreshReport,
      vatGenerateRequestData,
      reportId
    );
    const err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const useSummarized = transactionsType === 'SUMMARISED';
    const vatReportData: VatReportData =
      Object.keys(response.data).length > 0
        ? response.data
        : ({ empty: true } as VatReportData);

    // Recap statements aggregate by customer: refresh can change contact
    // details (VAT number, VIES status, amounts) without adding rows.
    const isRecap =
      !!vatReportData.recap || Array.isArray(vatReportData.contacts);

    if (isRecap && Array.isArray(vatReportData.contacts)) {
      const contactsChanged = haveRecapContactsChanged(
        previousReportData?.contacts,
        vatReportData.contacts
      );
      if (contactsChanged) {
        yield call(showUpdatedDataMessage);
      }
    } else if (!skipJournalPages) {
      const newTransactions = vatReportData.transactionsSize || 0;
      const newExceptions = vatReportData.exceptionsSize || 0;
      const difference = newTransactions + newExceptions - totalTransactions;
      yield call(showNewTransactionsMessage, difference);
    }

    yield put(generateReportSuccess(vatReportData, true, true));
    if (!skipJournalPages) {
      yield call(
        callInitialPages,
        reportId,
        prefetchTransactionsPageSize,
        prefetchExceptionsPageSize,
        useSummarized
      );
    }
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
  }
}

const getPageSize = (userPreferedSize: any) => {
  return typeof userPreferedSize === 'number' && userPreferedSize > 0
    ? userPreferedSize
    : 10;
};

function* showNewTransactionsMessage(
  difference: number,
  markNotSaved = true
) {
  if (difference > 0) {
    const message: MessageData = {
      title: i18n.t('notifications.system-message'),
      description: i18n.t('common.new-transactions-save-or-revert', {
        count: difference,
      }),
      type: MessageStates.INFO,
    };
    yield put(turnMessageOn(message));
    if (markNotSaved) {
      yield put(changeStatusAfterUpdate(DataLogStatus.NOT_SAVED));
    }
  }
}

function* showUpdatedDataMessage() {
  const message: MessageData = {
    title: i18n.t('notifications.system-message'),
    description: i18n.t('common.updated-data-save-or-revert'),
    type: MessageStates.INFO,
  };
  yield put(turnMessageOn(message));
  yield put(changeStatusAfterUpdate(DataLogStatus.NOT_SAVED));
}

/** Stable fingerprint of a recap contact for content comparison. */
function contactFingerprint(contact: DataContact): string {
  return [
    contact.providerContactId ?? '',
    contact.name ?? '',
    contact.country ?? '',
    contact.taxNumber ?? '',
    contact.viesStatus ?? '',
    contact.goodsSum ?? 0,
    contact.servicesSum ?? 0,
    contact.triangularTradeSum ?? 0,
    contact.isTriangularTrade ?? false,
  ].join('|');
}

/**
 * Detects recap content changes (row count or field updates such as VAT
 * number / VIES status) so refresh can mark the return as not saved.
 */
function haveRecapContactsChanged(
  previous: DataContact[] | null | undefined,
  next: DataContact[] | null | undefined
): boolean {
  const prevList = previous ?? [];
  const nextList = next ?? [];

  if (prevList.length === 0 && nextList.length === 0) {
    return false;
  }
  if (prevList.length !== nextList.length) {
    return true;
  }

  const prevFingerprints = prevList.map(contactFingerprint).sort();
  const nextFingerprints = nextList.map(contactFingerprint).sort();

  return prevFingerprints.some((fp, index) => fp !== nextFingerprints[index]);
}

function* callInitialPages(
  reportId,
  prefetchTransactionsPageSize,
  prefetchExceptionsPageSize,
  useSummarized
) {
  const transactionsPageSize = getPageSize(prefetchTransactionsPageSize);
  const exceptionsPageSize = getPageSize(prefetchExceptionsPageSize);

  yield put(
    getDataPageRequest(
      reportId,
      1,
      transactionsPageSize,
      JournalType.TRANSACTION,
      useSummarized ? 'summarised' : 'detailed'
    )
  );
  yield take(SET_CURRENT_JOURNALS_PAGE);
  yield put(
    getDataPageRequest(
      reportId,
      1,
      exceptionsPageSize,
      JournalType.EXCEPTION,
      'detailed'
    )
  );
}

